import { useTasks } from '../context/TaskContext'
import TaskList from '../components/TaskList'
import StatsCard from '../components/StatsCard'
import ProgressRing from '../components/ProgressRing'
import { formatDate } from '../utils/dateUtils'
import { motion } from 'framer-motion'
import { 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  Flame,
  AlertCircle,
  Sparkles,
  Calendar,
  ArrowRight
} from 'lucide-react'

function Dashboard() {
  const { todayTasks, stats, loading } = useTasks()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-indigo-200 dark:border-indigo-900 border-t-indigo-600 rounded-full"
        />
      </div>
    )
  }

  const completedToday = todayTasks.filter(t => t.status === 'completed').length
  const totalToday = todayTasks.length
  const todayProgress = totalToday > 0 ? (completedToday / totalToday) * 100 : 0

  // Get high priority tasks
  const highPriorityTasks = todayTasks.filter(t => t.priority === 'high' && t.status !== 'completed')
  
  // Get pending tasks
  const pendingTasks = todayTasks.filter(t => t.status !== 'completed')

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  }

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Welcome Section */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Good {getGreeting()}, Mills! 👋
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Here&apos;s what&apos;s happening with your tasks today
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
          <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <span className="text-sm font-medium text-indigo-900 dark:text-indigo-300">
            {formatDate(new Date())}
          </span>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Today's Tasks"
          value={totalToday}
          subtitle={`${completedToday} completed`}
          icon={CheckCircle2}
          color="indigo"
          trend={totalToday > 0 ? Math.round((completedToday / totalToday) * 100) : 0}
        />
        <StatsCard
          title="Pending"
          value={stats?.pending || 0}
          subtitle={`${stats?.highPriority || 0} high priority`}
          icon={Clock}
          color="amber"
        />
        <StatsCard
          title="Completed"
          value={stats?.completed || 0}
          subtitle={`${stats?.completionRate || 0}% rate`}
          icon={TrendingUp}
          color="emerald"
        />
        <StatsCard
          title="Streak"
          value={stats?.streak?.current || 0}
          subtitle={`Best: ${stats?.streak?.longest || 0} days`}
          icon={Flame}
          color="orange"
        />
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Tasks */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <div className="card bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Today&apos;s Tasks
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {pendingTasks.length} pending, {completedToday} completed
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ x: 4 }}
                className="text-sm font-medium text-indigo-600 dark:text-indigo-400 flex items-center gap-1 hover:underline"
              >
                View all
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
            <TaskList 
              tasks={todayTasks} 
              emptyMessage="No tasks for today. Add one to get started!" 
            />
          </div>
        </motion.div>

        {/* Sidebar */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Progress */}
          <div className="card bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Daily Progress
            </h3>
            <div className="flex flex-col items-center">
              <ProgressRing progress={todayProgress} size={140} strokeWidth={12} color="white" />
              <p className="text-center text-indigo-100 mt-4">
                {completedToday === totalToday && totalToday > 0
                  ? "🎉 All done for today!"
                  : `${totalToday - completedToday} tasks remaining`}
              </p>
            </div>
          </div>

          {/* High Priority */}
          {highPriorityTasks.length > 0 && (
            <div className="card border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                High Priority
              </h3>
              <div className="space-y-3">
                {highPriorityTasks.slice(0, 3).map((task, index) => (
                  <motion.div 
                    key={task.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-red-100 dark:border-red-800/30"
                  >
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {task.title}
                      </p>
                      {task.dueTime && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Due at {task.dueTime}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
                {highPriorityTasks.length > 3 && (
                  <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                    +{highPriorityTasks.length - 3} more
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Quick Tips */}
          <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              Pro Tip
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Try using natural language when creating tasks! Type things like 
              <span className="font-medium text-indigo-600 dark:text-indigo-400"> &quot;Meeting tomorrow at 2pm&quot; </span> 
              or <span className="font-medium text-indigo-600 dark:text-indigo-400">&quot;Grocery shopping in 2 hours&quot;</span>.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

export default Dashboard
